import { PadelScore, WinnerTeam } from "../types/Result";
import { FootballMember, FootballTeam, StandingsCriteria, Tournament, TournamentBracket } from "../types/Tournaments";
import client from "./axios";

export type PartialPair = {
  id: string;
  player1Name: string;
  player2Name: string;
  // Americano: id dei due giocatori del team (coppia estemporanea di giornata).
  player1Id?: string;
  player2Id?: string;
  rank?: number;
  // Padel stats
  setsWon?: number;
  setsLost?: number;
  gamesWon?: number;
  gamesLost?: number;
  // Football stats
  wins?: number;
  draws?: number;
  losses?: number;
  gamesPlayed?: number;
  goalsFor?: number;
  goalsAgainst?: number;
};

export type FootballGoal = {
  playerId: string;
  playerName: string;
  teamId: string;
  minute?: number;
  autogoal?: boolean;
  assistPlayerId?: string;
  assistPlayerName?: string;
  // Basket: punti segnati dal giocatore (una voce per giocatore).
  points?: number;
};

// Metodo di abbinamento delle ripescate al primo turno del Gold.
export type BestExtraMode = "vsSeeds" | "amongThemselves";

export type TournamentOrganizer = {
  userId: string;
  name: string;
  familyName?: string;
  email?: string;
};

export type TopScorer = {
  playerId: string;
  playerName: string;
  teamId: string;
  goals: number;
};

export type TopAssist = {
  playerId: string;
  playerName: string;
  teamId: string;
  assists: number;
};

export type BracketMatch = {
  id: string;
  home: PartialPair | "BYE";
  away: PartialPair | "BYE";
  score?: PadelScore[];
  winnerTeam?: WinnerTeam;
  scorers?: FootballGoal[];
  // Calcio: punteggio dei calci di rigore dopo un pareggio (solo se disputati).
  shootout?: PadelScore;
  // Punti assegnati ai rigori (team1/team2) nel girone.
  shootoutPoints?: PadelScore;
  dateTime?: string;
  // Partita in corso ("LIVE"): pallino rosso pulsante nell'app.
  live?: boolean;
};

export type GironeDay = {
  giornata: number;
  matches: BracketMatch[];
};

export type BracketRound = {
  name: string;
  matches: BracketMatch[];
};

export type FullBracket = BracketRound[];

export type LabeledGirone = {
  name: string;
  pairs: PartialPair[];
  calendario?: GironeDay[];
};

export type TournamentSchedule = {
  gironi?: LabeledGirone[];
  goldBracket: FullBracket;
  silverBracket?: FullBracket;
  rounds?: BracketRound[];
};

const tournamentAdminBaseUrl =
  "https://internal-api.getmoveapp.com/api/v1/admin/tournaments";
const tournamentBaseUrl =
  "https://internal-api.getmoveapp.com/api/v1/tournaments";
const adminFootballTeamBaseUrl =
  "https://internal-api.getmoveapp.com/api/v1/admin/football-teams";

export const Tournaments = {
  getTournaments: (params?: URLSearchParams) =>
    client.get<{ list: Tournament[] }>(`${tournamentAdminBaseUrl}`, { params }),
  getTournament: (id: string) =>
    client.get<{ tournament: Tournament }>(`${tournamentBaseUrl}/${id}`),
  deleteTournament: (id: string) =>
    client.delete<{ result: string }>(`${tournamentAdminBaseUrl}/${id}`),
  getTournamentImage: (id: string) =>
    client.get<{ url: string }>(`${tournamentBaseUrl}/get-image-by-path/${id}`),
  createTournament: (fd: FormData) =>
    client.post<{ tournament: Tournament }>(
      `${tournamentAdminBaseUrl}/create`,
      fd
    ),
  updateTournament: (id: string, fd: FormData) =>
    client.put<{ tournament: Tournament }>(
      `${tournamentAdminBaseUrl}/${id}`,
      fd
    ),
  generateTabellone: (body: any) =>
    client.post<TournamentSchedule>(
      `${tournamentAdminBaseUrl}/generate-brackets`,
      body
    ),
  saveTabellone: (tournamentId: string, body: any) =>
    client.post<any>(
      `${tournamentAdminBaseUrl}/${tournamentId}/save-brackets`,
      body
    ),
  getTabellone: (tournamentId: string) =>
    client.get<TournamentBracket>(
      `${tournamentAdminBaseUrl}/${tournamentId}/brackets`
    ),
  updateTabelloneResult: (
    tournamentId: string,
    body: {
      girone?: string;
      matchId: string;
      score: PadelScore[];
      winnerTeam?: WinnerTeam;
      gold?: boolean;
      silver?: boolean;
      football?: boolean;
      scorers?: FootballGoal[];
      // Calcio: punteggio dei calci di rigore dopo un pareggio.
      shootout?: PadelScore;
      dateTime?: string;
      dateOnly?: boolean;
      reset?: boolean;
      // Accende/spegne lo stato "LIVE" (true = LIVE, false = termina partita).
      live?: boolean;
    }
  ) =>
    client.post<TournamentBracket>(
      `${tournamentAdminBaseUrl}/${tournamentId}/update-result`,
      body
    ),
  // Invia una notifica personalizzata (titolo + testo) agli utenti GetMove
  // iscritti al torneo (calcio: presidenti + membri registrati; padel/tennis:
  // entrambi i giocatori delle coppie).
  notifyParticipants: (
    tournamentId: string,
    body: { title: string; text: string }
  ) =>
    client.post<{ recipients: number; pushed: number }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/notify-participants`,
      body
    ),
  // Cambia le impostazioni di un tabellone già generato (criterio della
  // classifica e rigori nei pareggi) senza toccare risultati o calendario.
  // I campi dei rigori sono opzionali: si inviano solo se vanno cambiati.
  updateBracketSettings: (
    tournamentId: string,
    standingsCriteria: StandingsCriteria,
    shootoutSettings?: {
      shootout: boolean;
      shootoutWinPoints: number;
      shootoutLosePoints: number;
    }
  ) =>
    client.post<{
      standingsCriteria: StandingsCriteria;
      shootout?: boolean;
      shootoutWinPoints?: number;
      shootoutLosePoints?: number;
    }>(`${tournamentAdminBaseUrl}/${tournamentId}/bracket-settings`, {
      standingsCriteria,
      ...(shootoutSettings || {}),
    }),
  // Cambia le impostazioni della fase finale di un torneo a gironi già in
  // corso (qualificate al Gold per girone, Silver sì/no, finale 3°/4° posto):
  // il backend rigenera SOLO i tabelloni Gold/Silver, gironi e classifiche
  // restano intatti. I risultati già inseriti nella fase finale si perdono.
  updateFinalPhase: (
    tournamentId: string,
    body: {
      coppieGold: number;
      silver: boolean;
      thirdPlace?: boolean;
      // Ripescaggio: n migliori (coppieGold+1)-esime nel Gold (solo senza Silver).
      bestExtra?: number;
      // Abbinamento delle ripescate: contro le migliori prime o spareggio tra loro.
      bestExtraMode?: BestExtraMode;
    }
  ) =>
    client.post<{
      coppieGold: number;
      silver: boolean;
      thirdPlace: boolean;
      bestExtra: number;
      bestExtraMode: BestExtraMode;
    }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/final-phase-settings`,
      body
    ),
  // Scambia due coppie/squadre fra due partite del PRIMO turno di un tabellone
  // (drag & drop): solo fra partite non ancora giocate; i walkover contro i BYE
  // vengono ricalcolati e i turni successivi riallineati.
  swapBracketEntries: (
    tournamentId: string,
    body: {
      bracket: "gold" | "silver" | "rounds";
      fromMatchId: string;
      fromSide: "home" | "away";
      toMatchId: string;
      toSide: "home" | "away";
    }
  ) =>
    client.post<{ bracket: FullBracket }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/swap-entries`,
      body
    ),
  populateBrackets: (
    tournamentId: string,
    body: {
      roundRobin: LabeledGirone[];
      brackets: FullBracket;
      gold?: boolean;
      silver?: boolean;
    }
  ) =>
    client.post<any>(
      `${tournamentAdminBaseUrl}/${tournamentId}/populate-brackets`,
      body
    ),
  endTournament: (id: string) =>
    client.post<{ message: string }>(`${tournamentAdminBaseUrl}/${id}/end`),
  // Riallinea i nomi nel tabellone (squadre di calcio o giocatori delle coppie)
  // dopo una rinomina o un cambio di giocatori: `renamed` è quante squadre o
  // coppie sono state aggiornate.
  refreshBracketNames: (tournamentId: string) =>
    client.post<{ renamed: number }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/refresh-names`
    ),
  // Punta la voce `fromId` del tabellone sulla squadra/coppia `toId`: serve
  // quando l'aggancio per id non esiste o non è più valido. `replaced` è quante
  // occorrenze (partite, classifiche) sono state riscritte.
  remapBracketEntry: (tournamentId: string, fromId: string, toId: string) =>
    client.post<{ replaced: number }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/remap-entry`,
      { fromId, toId }
    ),
  setOrganizers: (tournamentId: string, organizers: TournamentOrganizer[]) =>
    client.put<{ tournament: any }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/organizers`,
      { organizers }
    ),
  getTopScorers: (tournamentId: string) =>
    client.get<{ scorers: TopScorer[] }>(
      `${tournamentBaseUrl}/${tournamentId}/top-scorers`
    ),
  getTopAssists: (tournamentId: string) =>
    client.get<{ assists: TopAssist[] }>(
      `${tournamentBaseUrl}/${tournamentId}/top-assists`
    ),
};

export const AdminFootballTeams = {
  getTeams: (tournamentId: string) =>
    client.get<{ teams: FootballTeam[] }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/football-teams`
    ),
  // Crea una squadra (rosa vuota) lato admin.
  createTeam: (tournamentId: string, name: string) =>
    client.post<{ team: FootballTeam }>(
      `${tournamentAdminBaseUrl}/${tournamentId}/football-teams`,
      { name }
    ),
  deleteTeam: (teamId: string) =>
    client.delete<{ message: string }>(`${adminFootballTeamBaseUrl}/${teamId}`),
  removeMember: (teamId: string, userId: string) =>
    client.put<{ team: FootballTeam }>(
      `${adminFootballTeamBaseUrl}/${teamId}/remove-member`,
      { userId }
    ),
  // Sostituisce l'intera rosa (aggiungi/rimuovi utenti e ospiti).
  updateMembers: (teamId: string, members: FootballMember[]) =>
    client.put<{ team: FootballTeam }>(
      `${adminFootballTeamBaseUrl}/${teamId}/members`,
      { members }
    ),
  // Rinomina la squadra (i membri non vengono toccati).
  renameTeam: (teamId: string, name: string) =>
    client.put<{ team: FootballTeam }>(
      `${adminFootballTeamBaseUrl}/${teamId}/members`,
      { name }
    ),
};
